import { useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';

import { devicesApi } from '../../services/api';
import { useSocket } from '../../hooks/useSocket';
import { DeviceCluster } from '../../components/map/DeviceCluster';

// Fix Leaflet default marker icon
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

type VehicleType = 'MOTORCYCLE' | 'CAR' | 'TRUCK' | 'OTHER';

interface DeviceWithPosition {
  id: string;
  imei: string;
  name: string | null;
  isOffline?: boolean;
  isOverdue: boolean;
  isInRepo: boolean;
  isAggressive?: boolean;
  vehicleType?: VehicleType | null;
  vehiclePlate?: string | null;
  lastPosition: {
    latitude: number;
    longitude: number;
    speed: number | null;
    deviceTime: string;
  } | null;
}

type FilterType = 'all' | 'online' | 'offline' | 'overdue' | 'repo';

export function DashboardPage() {
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: devicesResponse, isLoading } = useQuery({
    queryKey: ['devices', { limit: 500 }],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 500 });
      // All queries under the ['devices', ...] key MUST return Device[] —
      // pages sharing the key receive each other's cached data as-is.
      const list = response.data.data;
      return (Array.isArray(list) ? list : list?.devices || []) as DeviceWithPosition[];
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const { lastPosition } = useSocket(selectedDevice ? [selectedDevice] : []);

  // Get devices array from response
  const devicesData = Array.isArray(devicesResponse) ? devicesResponse : [];

  // Update device positions from WebSocket
  const devices = devicesData.map((device) => {
    if (device.id === lastPosition?.deviceId) {
      return {
        ...device,
        lastPosition: {
          ...device.lastPosition,
          latitude: lastPosition.latitude,
          longitude: lastPosition.longitude,
          speed: lastPosition.speed,
          deviceTime: lastPosition.deviceTime,
        },
      };
    }
    return device;
  });

  // Apply filters
  const filteredDevices = devices?.filter((device) => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        device.imei.toLowerCase().includes(search) ||
        (device.name?.toLowerCase().includes(search) ?? false) ||
        (device.vehiclePlate?.toLowerCase().includes(search) ?? false);
      if (!matchesSearch) return false;
    }

    // Status filter
    switch (filter) {
      case 'online':
        return !device.isOffline && device.lastPosition;
      case 'offline':
        return device.isOffline || !device.lastPosition;
      case 'overdue':
        return device.isOverdue;
      case 'repo':
        return device.isInRepo;
      default:
        return true;
    }
  });

  const devicesWithPosition = filteredDevices?.filter((d) => d.lastPosition) || [];

  // Calculate stats
  const stats = {
    total: devices?.length || 0,
    online: devices?.filter((d) => !d.isOffline && d.lastPosition).length || 0,
    offline: devices?.filter((d) => d.isOffline || !d.lastPosition).length || 0,
    overdue: devices?.filter((d) => d.isOverdue).length || 0,
    repo: devices?.filter((d) => d.isInRepo).length || 0,
  };

  // Calculate map center
  const center: [number, number] = devicesWithPosition.length > 0
    ? [
        devicesWithPosition[0].lastPosition!.latitude,
        devicesWithPosition[0].lastPosition!.longitude,
      ]
    : [18.4861, -69.9312]; // Default: Santo Domingo

  return (
    <div className="h-full flex flex-col">
      {/* Stats bar */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setFilter('all')}
            aria-pressed={filter === 'all'}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'all'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Todos ({stats.total})
          </button>
          <button
            onClick={() => setFilter('online')}
            aria-pressed={filter === 'online'}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'online'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
            Online ({stats.online})
          </button>
          <button
            onClick={() => setFilter('offline')}
            aria-pressed={filter === 'offline'}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'offline'
                ? 'bg-gray-200 text-gray-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span className="inline-block w-2 h-2 rounded-full bg-gray-400 mr-1.5"></span>
            Offline ({stats.offline})
          </button>
          <button
            onClick={() => setFilter('overdue')}
            aria-pressed={filter === 'overdue'}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'overdue'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Atrasados ({stats.overdue})
          </button>
          <button
            onClick={() => setFilter('repo')}
            aria-pressed={filter === 'repo'}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'repo'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Incaute ({stats.repo})
          </button>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Device list sidebar */}
        <div className="w-80 bg-white border-r overflow-hidden flex flex-col">
          <div className="p-4 border-b">
            <input
              type="text"
              placeholder="Buscar por IMEI, nombre o placa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-2">
              Mostrando {filteredDevices?.length || 0} dispositivos
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">Cargando...</div>
            ) : filteredDevices?.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No hay dispositivos que coincidan
              </div>
            ) : (
              <div className="divide-y">
                {filteredDevices?.map((device) => (
                  <div
                    key={device.id}
                    onClick={() => setSelectedDevice(device.id)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedDevice === device.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">
                        {device.name || device.imei}
                      </span>
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          device.isOffline ? 'bg-gray-400' : 'bg-green-500'
                        }`}
                      />
                    </div>
                    {device.vehiclePlate && (
                      <p className="text-sm text-gray-600 mt-1 font-medium">
                        {device.vehiclePlate}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      IMEI: {device.imei}
                    </p>
                    {device.lastPosition && (
                      <p className="text-xs text-gray-400">
                        {device.lastPosition.speed?.toFixed(0) || 0} km/h
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {device.isOverdue && (
                        <span className="inline-block px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                          Atrasado
                        </span>
                      )}
                      {device.isInRepo && (
                        <span className="inline-block px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                          Incaute
                        </span>
                      )}
                      {device.isAggressive && (
                        <span className="inline-block px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                          Agresivo
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="flex-1">
          <MapContainer
            center={center}
            zoom={12}
            className="h-full w-full"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <DeviceCluster
              devices={devicesWithPosition}
              onDeviceClick={(id) => setSelectedDevice(id)}
            />
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
